/**
 * BrandIcons — minimal SVG icons mapped to common platform glyphs.
 * Used in the Library page so each integration shows its own brand
 * logo instead of a generic lucide icon.
 *
 * Iter40 user requests:
 *   - "the library icon should be minimal books leaning against each
 *     other, like the one in spotify."
 *   - "inside the library, the integrations should use their
 *     platform's icon. spotify should use the spotify icon, etc."
 *
 * All icons render as 16×16 by default and accept a `className`.
 * Colors are intentionally currentColor-driven (so `text-…` classes
 * affect the strokes/fills) UNLESS the icon is naturally branded
 * (Spotify green ring, Google quadrants, etc.) — in those cases we
 * hardcode the color since the brand is the point.
 */

export function BooksIcon({ className = "w-4 h-4" }) {
  // Three books leaning against each other — Spotify's library aesthetic.
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="6" width="3.5" height="14" rx="0.5" />
      <rect x="8" y="4" width="3.5" height="16" rx="0.5" />
      <g transform="rotate(15 16 14)">
        <rect x="14.5" y="6" width="3.5" height="14" rx="0.5" />
      </g>
    </svg>
  );
}

export function GoogleCalendarIcon({ className = "w-4 h-4" }) {
  return (
    <img
      src="/google-calendar-icon.png"
      alt="Google Calendar"
      className={className}
      draggable={false}
    />
  );
}

export function MicrosoftIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 23 23" className={className}>
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

export function GoogleDocsIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 47 65" className={className}>
      <path d="M29.4 0H4.7A4.69 4.69 0 0 0 0 4.7v55.6A4.69 4.69 0 0 0 4.7 65h37.6a4.69 4.69 0 0 0 4.7-4.7V18.3L29.4 0z" fill="#4285F4" />
      <path d="M29.4 0v13.6c0 2.6 2.1 4.7 4.7 4.7H47L29.4 0z" fill="#A1C2FA" />
      <path d="M11.7 28.2v3h23.5v-3H11.7zm0 7v3h23.5v-3H11.7zm0 7v3h17.6v-3H11.7z" fill="#fff" />
    </svg>
  );
}

export function GoogleDriveIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 87.3 78" className={className}>
      <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3L27.5 51H0c0 1.55.4 3.1 1.2 4.5l5.4 11.35z" fill="#0066DA" />
      <path d="M43.65 25L29.9 0C28.55.8 27.4 1.9 26.6 3.3L1.2 46.5A9 9 0 0 0 0 51h27.5l16.15-26z" fill="#00AC47" />
      <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8L73.55 76.8z" fill="#EA4335" />
      <path d="M43.65 25L57.4 0c-1.35-.8-2.9-1.2-4.5-1.2H34.4c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D" />
      <path d="M59.8 51H27.5L13.75 76.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2L59.8 51z" fill="#2684FC" />
      <path d="M73.4 26.5L48 3.3c-1.35-.8-2.9-1.2-4.5-1.2l.15-.1L43.65 25 57.4 51h29.9c0-1.55-.4-3.1-1.2-4.5L73.4 26.5z" fill="#FFBA00" />
    </svg>
  );
}

export function SpotifyIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <circle cx="12" cy="12" r="12" fill="#1DB954" />
      <path d="M16.7 16.6c-.2.3-.6.4-.9.2-2.5-1.5-5.6-1.8-9.3-1-.4.1-.7-.2-.8-.5-.1-.4.2-.7.5-.8 4-.9 7.5-.5 10.3 1.2.3.2.4.6.2.9zm1.3-2.7c-.3.4-.7.5-1.1.3-2.8-1.7-7.1-2.2-10.4-1.2-.4.1-.9-.1-1-.5-.1-.4.1-.9.5-1 3.8-1.2 8.5-.6 11.7 1.4.4.2.5.7.3 1zm.1-2.8C14.7 9 8.5 8.8 5.4 9.7c-.5.2-1.1-.1-1.2-.6-.2-.5.1-1.1.6-1.2 3.6-1.1 10.4-.9 14.3 1.4.5.3.6.9.4 1.4-.3.4-1 .6-1.4.4z" fill="#000" />
    </svg>
  );
}

export function ClickUpIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="cu-a" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FF1744"/>
          <stop offset="50%" stopColor="#FF6D00"/>
          <stop offset="100%" stopColor="#FFD600"/>
        </linearGradient>
        <linearGradient id="cu-b" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00B0FF"/>
          <stop offset="100%" stopColor="#7C4DFF"/>
        </linearGradient>
      </defs>
      <path d="M8 44.8L18.4 35.2C21.6 40 25.6 42.4 32 42.4C38.4 42.4 42.4 40 45.6 35.2L56 44.8C51.2 52 42.4 57.6 32 57.6C21.6 57.6 12.8 52 8 44.8Z" fill="url(#cu-b)"/>
      <path d="M32 6.4L8 25.6L18.4 33.6C21.6 28.8 25.6 25.6 32 25.6C38.4 25.6 42.4 28.8 45.6 33.6L56 25.6L32 6.4Z" fill="url(#cu-a)"/>
    </svg>
  );
}

export function SlackIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52z" fill="#E01E5A"/>
      <path d="M6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A"/>
      <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834z" fill="#36C5F0"/>
      <path d="M8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0"/>
      <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834z" fill="#2EB67D"/>
      <path d="M17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D"/>
      <path d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52z" fill="#ECB22E"/>
      <path d="M15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" fill="#ECB22E"/>
    </svg>
  );
}
